import React, { useState, useEffect, useRef } from 'react';
import { Lock, Unlock, KeyRound, X, Check, AlertCircle, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { Note } from '../../types';

interface NotePinModalProps {
  isOpen: boolean;
  mode: 'set' | 'unlock' | 'remove' | 'change';
  note?: Note | null;
  masterPin: string | null;
  onClose: () => void;
  onSuccess: (newPin?: string) => void;
}

export const NotePinModal: React.FC<NotePinModalProps> = ({
  isOpen,
  mode,
  note,
  masterPin,
  onClose,
  onSuccess,
}) => {
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setPin('');
      setConfirmPin('');
      setError('');
      setShowPin(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, mode, note]);

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const triggerError = (msg: string) => {
    setError(msg);
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 500);
  };

  const isSettingPin = mode === 'set' || mode === 'change';

  const handleKeypadPress = (num: string) => {
    if (isSettingPin && pin.length >= 4 && confirmPin.length < 4) {
      setConfirmPin((prev) => prev + num);
      return;
    }
    if (pin.length < 6) {
      setPin((prev) => prev + num);
    }
  };

  const handleBackspace = () => {
    if (isSettingPin && confirmPin.length > 0) {
      setConfirmPin((prev) => prev.slice(0, -1));
    } else {
      setPin((prev) => prev.slice(0, -1));
    }
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError('');

    if (isSettingPin) {
      if (pin.length < 4) {
        triggerError('PIN must be at least 4 digits');
        return;
      }
      if (confirmPin && pin !== confirmPin) {
        triggerError('PINs do not match');
        return;
      }
      onSuccess(pin);
      onClose();
      return;
    }

    if (mode === 'unlock' || mode === 'remove') {
      if (!pin) {
        triggerError('Please enter your PIN');
        return;
      }
      const expectedPin = note?.pin || masterPin;
      if (expectedPin && expectedPin !== pin) {
        triggerError('Incorrect PIN code. Try again.');
        return;
      }
      onSuccess(pin);
      onClose();
    }
  };

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/65 backdrop-blur-sm animate-fade-in"
    >
      <div
        className={`w-full max-w-sm bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl p-6 relative animate-scale-in ${
          isShaking ? 'animate-bounce' : ''
        }`}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Icon & Header */}
        <div className="text-center space-y-2 mb-5">
          <div
            className={`w-14 h-14 rounded-2xl mx-auto flex items-center justify-center shadow-lg ${
              isSettingPin
                ? 'bg-purple-500/10 text-purple-600 dark:text-purple-400 shadow-purple-500/10'
                : mode === 'unlock'
                ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 shadow-amber-500/10'
                : 'bg-rose-500/10 text-rose-600 dark:text-rose-400 shadow-rose-500/10'
            }`}
          >
            {isSettingPin ? (
              <KeyRound className="w-7 h-7" />
            ) : mode === 'unlock' ? (
              <Lock className="w-7 h-7" />
            ) : (
              <Unlock className="w-7 h-7" />
            )}
          </div>

          <h3 className="text-lg font-black text-slate-900 dark:text-white">
            {mode === 'set'
              ? 'Set Notes Master PIN'
              : mode === 'change'
              ? 'Change Master PIN'
              : mode === 'unlock'
              ? 'Unlock Note'
              : 'Remove Note Lock'}
          </h3>

          {note && (
            <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1 px-2 font-medium">
              {note.title || 'Untitled Note'}
            </p>
          )}

          <p className="text-[11px] text-slate-400">
            {isSettingPin
              ? 'This single PIN will secure and unlock all your locked notes.'
              : 'Enter your Notes PIN to access this private note.'}
          </p>
        </div>

        {/* PIN Input Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <input
              ref={inputRef}
              type={showPin ? 'text' : 'password'}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder={isSettingPin ? 'Enter New PIN (4-6 digits)' : 'Enter PIN'}
              value={pin}
              onChange={(e) => {
                setPin(e.target.value.replace(/\D/g, ''));
                setError('');
              }}
              className="w-full text-center tracking-widest text-xl font-mono font-bold px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 placeholder-slate-400"
              autoFocus
            />

            <button
              type="button"
              onClick={() => setShowPin(!showPin)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-white"
            >
              {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {isSettingPin && pin.length >= 4 && (
            <div className="animate-fade-in">
              <input
                type={showPin ? 'text' : 'password'}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="Confirm PIN"
                value={confirmPin}
                onChange={(e) => {
                  setConfirmPin(e.target.value.replace(/\D/g, ''));
                  setError('');
                }}
                className="w-full text-center tracking-widest text-xl font-mono font-bold px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 placeholder-slate-400"
              />
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center gap-1.5 text-xs text-rose-500 font-bold animate-fade-in">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Quick On-Screen Number Keypad */}
          <div className="grid grid-cols-3 gap-2 pt-2">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => handleKeypadPress(num.toString())}
                className="py-2.5 bg-slate-50 dark:bg-slate-800/80 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-white text-base font-bold rounded-xl border border-slate-200/60 dark:border-slate-700/60 active:scale-95 transition-all"
              >
                {num}
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                setPin('');
                setConfirmPin('');
                setError('');
              }}
              className="py-2.5 text-xs font-bold text-slate-500 hover:text-slate-800 dark:hover:text-white bg-slate-50/50 dark:bg-slate-900 rounded-xl"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => handleKeypadPress('0')}
              className="py-2.5 bg-slate-50 dark:bg-slate-800/80 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-white text-base font-bold rounded-xl border border-slate-200/60 dark:border-slate-700/60 active:scale-95 transition-all"
            >
              0
            </button>
            <button
              type="button"
              onClick={handleBackspace}
              className="py-2.5 text-xs font-bold text-slate-500 hover:text-rose-500 bg-slate-50/50 dark:bg-slate-900 rounded-xl"
            >
              ⌫
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold transition-colors"
            >
              Cancel
            </button>

            <button
              type="submit"
              className={`flex-1 py-2.5 rounded-xl text-xs font-bold text-white shadow-md flex items-center justify-center gap-1.5 transition-all active:scale-95 ${
                isSettingPin
                  ? 'bg-purple-600 hover:bg-purple-700 shadow-purple-500/25'
                  : mode === 'unlock'
                  ? 'bg-amber-600 hover:bg-amber-700 shadow-amber-500/25'
                  : 'bg-rose-600 hover:bg-rose-700 shadow-rose-500/25'
              }`}
            >
              <Check className="w-4 h-4" />
              <span>
                {isSettingPin
                  ? 'Save PIN'
                  : mode === 'unlock'
                  ? 'Unlock'
                  : 'Remove Lock'}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
